import * as React from "react";
import { Button, Gapped, Loader, Modal } from "react-ui";

import { ICandyApiDraftClient } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import {
    EXPERT_ERRORS_PAGE_CANCEL_CLICK,
    EXPERT_ERRORS_PAGE_CANCEL_REQUEST,
    EXPERT_ERRORS_PAGE_DOWNLOAD_ERROR_LOG_CLICK,
    EXPERT_ERRORS_PAGE_GENERATE_NOTE_CLICK,
    EXPERT_ERRORS_RELOAD_ERROR_LOG_CLICK,
} from "../../metricsAction";
import { ErrorsList } from "../../FormParts/EmbeddedLog/ErrorList/ErrorsList";
import { trackAction } from "../../Internals/Metrics/MetricsProvider";
import { Icon } from "../../Icons/Icon";
import { ErrorLog } from "../../../Engine/Core/ErrorManager/Utils/Models";

import { MessageBoxProps } from "./MessageBox";
import { Footer, Loading, Message, NoteBody } from "./PrepareNote.styles";
import { MessageBoxType } from "./MessageBoxType";

export interface Props {
    errorLog: ErrorLog;
    handleClose: () => void;
    handleCreateNote: () => void;
    getExpertErrorLog: () => void;
    messageBox: React.ReactElement<MessageBoxProps> | undefined;
    candyApiDraftClient: ICandyApiDraftClient;
}

export class PrepareNote extends React.PureComponent<Props> {
    public static defaultProps = {
        errorLog: {
            isChecking: true,
        },
    };

    public render(): JSX.Element {
        const { errorLog } = this.props;
        const isChecking = errorLog.isChecking;
        const requestFailed = errorLog.requestFailed;
        const hasErrors = Boolean(errorLog.errorsTotalCount);
        const hasWarnings = Boolean(errorLog.warningsTotalCount);

        const hasLogErrors = Boolean(hasErrors || hasWarnings);
        const disabled = isChecking || requestFailed || hasLogErrors;

        return (
            <Modal onClose={this.handleCloseRequest}>
                <Modal.Header>Формирование финансового анализа</Modal.Header>
                <Modal.Body>
                    <NoteBody>
                        <Message>
                            Модификатор «Эксперт» может помочь в формировании анализа о финансовом положении
                            организации, сопоставимости данных за отчетный и предшествующий ему годы и др.
                            <p>Документ будет сформирован в формате pdf, его можно просмотреть и скачать.</p>
                        </Message>
                        {this.getMessageBox(hasLogErrors)}
                        {this.renderErrorLog()}
                    </NoteBody>
                </Modal.Body>
                <Modal.Footer panel>
                    <Footer>
                        <Gapped gap={10}>
                            <Button
                                data-tid="CreateNoteButton"
                                disabled={disabled}
                                onClick={this.handleCreateNote}
                                use="primary"
                                size="medium">
                                Сформировать
                            </Button>
                            <Button data-tid="CancelButton" onClick={this.handleClose} size="medium">
                                Отменить
                            </Button>
                        </Gapped>
                        {hasLogErrors && (
                            <Button
                                data-tid="DownloadErrorLog"
                                use="link"
                                size="medium"
                                onClick={this.handleDownloadErrorLog}>
                                <Icon name="NetDownloadIcon" /> Скачать результаты проверки
                            </Button>
                        )}
                    </Footer>
                </Modal.Footer>
            </Modal>
        );
    }

    private getMessageBox(hasLogErrors: boolean): React.ReactElement<MessageBoxProps> | undefined {
        return this.props.messageBox
            ? React.cloneElement(this.props.messageBox, {
                  type: this.getMessageType(hasLogErrors),
                  onGetExpertErrorLog: this.handleReloadErrorLog,
              })
            : undefined;
    }

    private readonly getMessageType = (hasLogErrors: boolean): MessageBoxType => {
        if (hasLogErrors) {
            return MessageBoxType.errorLog;
        }

        if (this.props.errorLog.requestFailed) {
            return MessageBoxType.error;
        }
        if (this.props.errorLog.isChecking) {
            return MessageBoxType.empty;
        }

        return MessageBoxType.success;
    };

    @trackAction(EXPERT_ERRORS_PAGE_DOWNLOAD_ERROR_LOG_CLICK)
    private readonly handleDownloadErrorLog = (): void => {
        const { errorLogName, propsName } = this.props.errorLog;

        if (!errorLogName || !propsName) {
            return;
        }

        const errorLogLink = this.props.candyApiDraftClient.getHrefToErrorLogPdf(errorLogName, propsName);

        window.open(errorLogLink);
    };

    @trackAction(EXPERT_ERRORS_PAGE_GENERATE_NOTE_CLICK)
    private readonly handleCreateNote = (): void => {
        this.props.handleCreateNote();
    };

    @trackAction(EXPERT_ERRORS_PAGE_CANCEL_CLICK)
    private readonly handleClose = (): void => {
        this.props.handleClose();
    };

    @trackAction(EXPERT_ERRORS_PAGE_CANCEL_REQUEST)
    private readonly handleCloseRequest = (): void => {
        this.props.handleClose();
    };

    @trackAction(EXPERT_ERRORS_RELOAD_ERROR_LOG_CLICK)
    private readonly handleReloadErrorLog = (): void => {
        this.props.getExpertErrorLog();
    };

    private renderErrorLog(): JSX.Element | undefined {
        if (this.props.errorLog.requestFailed) {
            return undefined;
        }

        if (this.props.errorLog.isChecking) {
            return (
                <Loading>
                    <Loader active type="big" caption="Проверяем..." />
                </Loading>
            );
        }

        return (
            <ErrorsList
                errorLog={this.props.errorLog}
                tooltipBindingPath={undefined}
                tooltipErrorId={undefined}
                showSuccesses={false}
                onFindAndFocusControl={() => async () => undefined}
                disableControlsTooltip={() => {
                    // noop
                }}
                enableControlsTooltip={() => {
                    // noop
                }}
                onNavigateToAttachment={() => Promise.resolve(undefined)}
                clearAttachmentErrorLog={() => undefined}
                enableControlsTooltipAttachment={() => undefined}
            />
        );
    }
}
